<template>
  <el-dialog
    :model-value="open"
    :title="modalTitle"
    width="min(640px, calc(100vw - 24px))"
    @update:model-value="emit('update:open', $event)"
    @cancel="handleClose"
  >
    <template #footer>
      <template v-if="mode === 'query'">
        <el-button @click="handleClose">关闭</el-button>
      </template>
      <template v-else>
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handleCreateSubmit">提交注册</el-button>
      </template>
    </template>

    <div v-if="mode === 'query'" class="vehicle-query-panel">
      <div class="query-toolbar">
        <el-input
          v-model="queryKeyword"
          :maxlength="20"
          placeholder="请输入要查询的车牌号"
          @pressEnter="handleQuery"
        />
        <el-button type="primary" :loading="queryLoading" @click="handleQuery">查询</el-button>
      </div>

      <el-skeleton v-if="queryLoading" active :paragraph="{ rows: 4 }" />
      <el-empty v-else-if="hasQueried && queryList.length === 0" description="没有结果" />

      <div v-else-if="queryList.length > 0" class="query-result-list">
        <div v-for="item in queryList" :key="item.key" class="query-result-card">
          <div class="query-main-row">
            <span class="query-chip">{{ item.vehicleTypeText }}</span>
            <span class="query-plate">{{ item.registrationNumber || '-' }}</span>
            <span class="query-cell">产品：{{ item.product || '-' }}</span>
            <span class="query-cell">轴数：{{ item.vehicleNumberaxes || '-' }}</span>
          </div>
          <div class="query-sub-row">
            <span class="query-cell">主车牌照号：{{ item.fleetNumber || '-' }}</span>
            <span class="query-cell">行驶证整备质量：{{ item.licenseCurbWeight || '-' }}</span>
            <span class="query-cell">整备质量：{{ item.curbWeight || '-' }}</span>
            <span class="query-cell">总质量：{{ item.totalWeight || '-' }}</span>
            <span class="query-cell">槽车水容积：{{ item.waterCapacity || '-' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="vehicle-create-panel">
      <el-form layout="vertical" class="vehicle-form-grid">
        <div class="vehicle-row two-col-row">
          <el-form-item label="车辆类型" required class="full-width-item">
            <el-select v-model="createForm.Vehicle_type" @change="handleVehicleTypeChange">
              <el-option :value="10" label="车头">车头</el-option>
              <el-option :value="20" label="车挂">车挂</el-option>
              <el-option :value="30" label="单车">单车</el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="车辆轴数" required class="full-width-item">
            <el-select v-model="createForm.Vehicle_numberaxes">
              <el-option value="2" label="2轴">2轴</el-option>
              <el-option value="3" label="3轴">3轴</el-option>
              <el-option value="4" label="4轴">4轴</el-option>
            </el-select>
          </el-form-item>
        </div>

        <div v-if="createForm.Vehicle_type === 10" class="vehicle-row two-col-row">
          <el-form-item label="主车牌照号" required class="full-width-item">
            <el-input v-model="createForm.FLEET_NUMBER" :maxlength="20" placeholder="请输入主车牌照号" />
          </el-form-item>

          <el-form-item label="行驶证整备质量（KG）" required class="full-width-item">
            <el-input v-model="createForm.License_curb_weight" :maxlength="5" placeholder="请输入行驶证整备质量" />
          </el-form-item>
        </div>

        <template v-else>
          <div class="vehicle-row two-col-row">
            <el-form-item :label="createForm.Vehicle_type === 20 ? '挂车牌照号' : '车牌照号'" required class="full-width-item">
              <el-input v-model="createForm.REGISTRATION_NUMBER" :maxlength="20" placeholder="请输入车牌照号" />
            </el-form-item>

            <el-form-item label="产品" required class="full-width-item">
              <el-select v-model="createForm.Product" :loading="productLoading" placeholder="请选择产品">
                <el-option v-for="item in productOptions" :key="item.Id" :value="item.Id" :label="item.Name">
                  {{ item.Name }}
                </el-option>
              </el-select>
            </el-form-item>
          </div>

          <div class="vehicle-row three-col-row">
            <el-form-item label="整备质量（KG）" required class="full-width-item">
              <el-input v-model="createForm.Curb_weight" :maxlength="5" placeholder="请输入整备质量" />
            </el-form-item>

            <el-form-item label="总质量（KG）" required class="full-width-item">
              <el-input v-model="createForm.Total_weight" :maxlength="5" placeholder="请输入总质量" />
            </el-form-item>

            <el-form-item label="槽车水容积" required class="full-width-item">
              <el-input v-model="createForm.Water_capacity" :maxlength="8" placeholder="请输入槽车水容积" />
            </el-form-item>
          </div>
        </template>
      </el-form>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { ElMessage as message } from 'element-plus';
import { createNewVehicle, getVehicle, getVehicleProducts } from '@/api/vehicle';
import { useUserStore } from '@/stores/user';
import type { VehicleCatalogProduct, VehicleCreatePayload, VehicleRecord } from '@/types/vehicle';
import { isGlobalHandledError } from '@/utils/request'

type VehicleModalMode = 'query' | 'create';

type NormalizedVehicleRecord = {
  key: string;
  vehicleType: number;
  vehicleTypeText: string;
  fleetNumber: string;
  registrationNumber: string;
  licenseCurbWeight: string;
  product: string;
  curbWeight: string;
  totalWeight: string;
  vehicleNumberaxes: string;
  waterCapacity: string;
};

const props = defineProps<{
  open: boolean;
  mode: VehicleModalMode;
}>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
}>();

const userStore = useUserStore();
const queryLoading = ref(false);
const submitLoading = ref(false);
const productLoading = ref(false);
const hasQueried = ref(false);
const queryKeyword = ref('');
const queryList = ref<NormalizedVehicleRecord[]>([]);
const productOptions = ref<VehicleCatalogProduct[]>([]);

const createForm = reactive({
  Vehicle_type: 10,
  FLEET_NUMBER: '',
  License_curb_weight: '',
  REGISTRATION_NUMBER: '',
  Product: 0,
  Curb_weight: '',
  Total_weight: '',
  Vehicle_numberaxes: '2',
  Water_capacity: ''
});

const modalTitle = computed(() => (props.mode === 'query' ? '车辆查询' : '车辆注册'));

const getCustomerGuid = () => String(userStore.userInfo?.Guid ?? userStore.userInfo?.guid ?? '').trim();

const trimValue = (value: string) => String(value || '').trim();

const resetQueryState = () => {
  hasQueried.value = false;
  queryKeyword.value = '';
  queryList.value = [];
};

const resetCreateForm = () => {
  createForm.Vehicle_type = 10;
  createForm.FLEET_NUMBER = '';
  createForm.License_curb_weight = '';
  createForm.REGISTRATION_NUMBER = '';
  createForm.Product = 0;
  createForm.Curb_weight = '';
  createForm.Total_weight = '';
  createForm.Vehicle_numberaxes = '2';
  createForm.Water_capacity = '';
};

const normalizeVehicleTypeText = (type: number) => {
  if (type === 10) return '车头';
  if (type === 20) return '车挂';
  if (type === 30) return '单车';
  return '未知类型';
};

const normalizeVehicleRecord = (item: VehicleRecord, index: number): NormalizedVehicleRecord => {
  const vehicleType = Number(item.Vehicle_type ?? item.vehicle_type ?? 0) || 0;
  return {
    key: String(item.Id ?? item.id ?? `${item.REGISTRATION_NUMBER ?? item.registration_NUMBER ?? 'vehicle'}-${index}`),
    vehicleType,
    vehicleTypeText: normalizeVehicleTypeText(vehicleType),
    fleetNumber: String(item.FLEET_NUMBER ?? item.fleet_NUMBER ?? ''),
    registrationNumber: String(item.REGISTRATION_NUMBER ?? item.registration_NUMBER ?? ''),
    licenseCurbWeight: String(item.License_curb_weight ?? item.license_curb_weight ?? ''),
    product: String(item.Product ?? item.product ?? ''),
    curbWeight: String(item.Curb_weight ?? item.curb_weight ?? ''),
    totalWeight: String(item.Total_weight ?? item.total_weight ?? ''),
    vehicleNumberaxes: String(item.Vehicle_numberaxes ?? item.vehicle_numberaxes ?? ''),
    waterCapacity: String(item.Water_capacity ?? item.water_capacity ?? '')
  };
};

const validateVehicleNo = (value: string) => /^[a-zA-Z0-9\u4e00-\u9fa5]+$/.test(value);

const validateWaterCapacity = (value: string) => /^[1-9]{1}\d{0,16}(\.\d{1,2})?$|^0(\.\d{1,2})?$/.test(value);

const ensureProductOptions = async () => {
  if (productOptions.value.length > 0 || productLoading.value) return;
  productLoading.value = true;
  try {
    const result = await getVehicleProducts();
    const list = Array.isArray(result?.Data)
      ? result.Data
      : Array.isArray(result?.data)
        ? result.data
        : [];
    productOptions.value = list.map((item) => ({
      Id: Number(item.Id ?? item.id ?? 0),
      Name: String(item.Name ?? item.name ?? '')
    }));
    const firstProduct = productOptions.value[0];
    if (firstProduct) {
      createForm.Product = firstProduct.Id;
    }
  } catch (error) {
    console.error('获取产品失败:', error);
    if (isGlobalHandledError(error)) return;
    message.error('获取产品失败，请稍后重试');
  } finally {
    productLoading.value = false;
  }
};

const handleQuery = async () => {
  const keyword = trimValue(queryKeyword.value);
  if (!keyword) {
    message.warning('请输入车牌号');
    return;
  }
  if (!validateVehicleNo(keyword)) {
    message.warning('牌照号只能包含中文、英文和数字');
    return;
  }

  const customerGuid = getCustomerGuid();
  if (!customerGuid) {
    message.warning('缺少客户信息，请重新登录后重试');
    return;
  }

  queryLoading.value = true;
  hasQueried.value = true;
  try {
    const result = await getVehicle({
      REGISTRATION_NUMBER: keyword,
      CustomerGuid: customerGuid
    });
    const list = Array.isArray(result)
      ? result
      : Array.isArray(result?.Data)
        ? result.Data
        : Array.isArray(result?.data)
          ? result.data
          : [];
    queryList.value = list.map((item, index) => normalizeVehicleRecord(item, index));
  } catch (error) {
    if (isGlobalHandledError(error)) return;
    console.error('查询车辆失败:', error);
    queryList.value = [];
  } finally {
    queryLoading.value = false;
  }
};

const handleVehicleTypeChange = async (value: number) => {
  createForm.Vehicle_type = value;
  createForm.FLEET_NUMBER = '';
  createForm.License_curb_weight = '';
  createForm.REGISTRATION_NUMBER = '';
  createForm.Product = 0;
  createForm.Curb_weight = '';
  createForm.Total_weight = '';
  createForm.Water_capacity = '';
  if (value !== 10) {
    await ensureProductOptions();
    const firstProduct = productOptions.value[0];
    if (firstProduct) {
      createForm.Product = firstProduct.Id;
    }
  }
};

const validateCreateForm = () => {
  const vehicleType = Number(createForm.Vehicle_type || 0);
  if (vehicleType === 10) {
    const fleetNumber = trimValue(createForm.FLEET_NUMBER);
    if (!fleetNumber) {
      message.warning('请输入主车牌照号');
      return false;
    }
    if (!validateVehicleNo(fleetNumber)) {
      message.warning('牌照号只能包含中文、英文和数字');
      return false;
    }

    const licenseCurbWeight = trimValue(createForm.License_curb_weight);
    const parsedLicenseWeight = Number(licenseCurbWeight);
    if (!licenseCurbWeight || Number.isNaN(parsedLicenseWeight) || parsedLicenseWeight < 5000 || parsedLicenseWeight > 20000) {
      message.warning('请输入有效行驶证整备质量，最小值5000，最大值20000');
      return false;
    }
    return true;
  }

  const registrationNumber = trimValue(createForm.REGISTRATION_NUMBER);
  if (!registrationNumber) {
    message.warning(vehicleType === 20 ? '请输入挂车牌照号' : '请输入车牌照号');
    return false;
  }
  if (!validateVehicleNo(registrationNumber)) {
    message.warning('牌照号只能包含中文、英文和数字');
    return false;
  }
  if (!Number(createForm.Product || 0)) {
    message.warning('请选择产品');
    return false;
  }

  const curbWeight = trimValue(createForm.Curb_weight);
  const totalWeight = trimValue(createForm.Total_weight);
  const waterCapacity = trimValue(createForm.Water_capacity);
  const parsedCurbWeight = Number(curbWeight);
  const parsedTotalWeight = Number(totalWeight);
  const parsedWaterCapacity = Number(waterCapacity);

  if (!curbWeight || Number.isNaN(parsedCurbWeight) || parsedCurbWeight < 1000 || parsedCurbWeight > 30000) {
    message.warning('请输入有效整备质量，最小值1000，最大值30000');
    return false;
  }
  if (!totalWeight || Number.isNaN(parsedTotalWeight) || parsedTotalWeight - parsedCurbWeight <= 0) {
    message.warning('总质量应大于整备质量');
    return false;
  }
  if (!validateWaterCapacity(waterCapacity)) {
    message.warning('槽车水容积只允许最多两位小数');
    return false;
  }

  if (vehicleType === 20) {
    if (parsedTotalWeight < 1000 || parsedTotalWeight > 50000) {
      message.warning('请输入有效总质量，最小值1000，最大值50000');
      return false;
    }
    if (!waterCapacity || Number.isNaN(parsedWaterCapacity) || parsedWaterCapacity < 10 || parsedWaterCapacity > 40) {
      message.warning('请输入有效槽车水容积，最小值10，最大值40');
      return false;
    }
  } else {
    if (parsedTotalWeight < 1000 || parsedTotalWeight > 32000) {
      message.warning('请输入有效总质量，最小值1000，最大值32000');
      return false;
    }
    if (!waterCapacity || Number.isNaN(parsedWaterCapacity) || parsedWaterCapacity < 0 || parsedWaterCapacity > 20) {
      message.warning('请输入有效槽车水容积，最小值0，最大值20');
      return false;
    }
  }

  return true;
};

const buildCreatePayload = (): VehicleCreatePayload | null => {
  const customerGuid = getCustomerGuid();
  if (!customerGuid) {
    message.warning('缺少客户信息，请重新登录后重试');
    return null;
  }

  const vehicleType = Number(createForm.Vehicle_type || 0);
  const fleetNumber = trimValue(createForm.FLEET_NUMBER);
  const registrationNumber = trimValue(createForm.REGISTRATION_NUMBER);

  return {
    OsAccountId: customerGuid,
    Vehicle_type: vehicleType,
    FLEET_NUMBER: vehicleType === 10 ? fleetNumber : registrationNumber,
    License_curb_weight: vehicleType === 10 ? trimValue(createForm.License_curb_weight) : '',
    REGISTRATION_NUMBER: vehicleType === 10 ? fleetNumber : registrationNumber,
    Product: vehicleType === 10 ? 0 : Number(createForm.Product || 0),
    Curb_weight: vehicleType === 10 ? '' : trimValue(createForm.Curb_weight),
    Total_weight: vehicleType === 10 ? '' : trimValue(createForm.Total_weight),
    Vehicle_numberaxes: trimValue(createForm.Vehicle_numberaxes),
    Water_capacity: vehicleType === 10 ? '' : trimValue(createForm.Water_capacity)
  };
};

const handleCreateSubmit = async () => {
  if (!validateCreateForm()) return;
  const payload = buildCreatePayload();
  if (!payload) return;

  submitLoading.value = true;
  try {
    await createNewVehicle(payload);
    message.success('车辆注册成功');
    resetCreateForm();
    emit('update:open', false);
  } catch (error) {
    if (isGlobalHandledError(error)) return;
    console.error('车辆注册失败:', error);
    message.error('车辆注册失败，请稍后重试');
  } finally {
    submitLoading.value = false;
  }
};

const handleClose = () => {
  emit('update:open', false);
};

watch(
  () => props.open,
  async (open) => {
    if (!open) return;
    if (props.mode === 'query') {
      resetQueryState();
      return;
    }

    resetCreateForm();
    await ensureProductOptions();
  }
);
</script>

<style scoped>
.query-toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.query-result-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.query-result-card {
  background: #f8fbff;
  border: 1px solid var(--cc-card-border);
  border-radius: 10px;
  padding: 12px 14px;
}

.query-main-row,
.query-sub-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
}

.query-sub-row {
  color: var(--cc-sub);
  margin-top: 8px;
}

.query-chip {
  background: #eaf3ff;
  border-radius: 999px;
  color: var(--cc-brand);
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
}

.query-plate {
  color: var(--cc-title);
  font-size: 16px;
  font-weight: 700;
}

.query-cell {
  color: var(--cc-sub);
  font-size: 13px;
}

.vehicle-form-grid {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.vehicle-row {
  display: grid;
  gap: 0 12px;
}

.two-col-row {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.three-col-row {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

:deep(.vehicle-form-grid .el-form-item) {
  align-items: stretch;
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-bottom: 0;
}

:deep(.vehicle-form-grid .el-form-item__label) {
  align-self: stretch;
  justify-content: flex-start;
  line-height: 1.4;
  padding: 0 0 6px;
  text-align: left;
  width: auto;
}

:deep(.vehicle-form-grid .el-form-item__content),
:deep(.vehicle-form-grid .el-select),
:deep(.vehicle-form-grid .el-input) {
  min-width: 0;
  width: 100%;
}

.form-note {
  color: var(--cc-sub);
  font-size: 12px;
  line-height: 1.5;
  margin-top: 6px;
}

@media (max-width: 768px) {
  .query-toolbar {
    flex-direction: column;
  }

  .two-col-row,
  .three-col-row {
    grid-template-columns: 1fr;
  }
}
</style>